<script setup lang='ts'>
import { timeFrom } from '@qcwp/utils'
import type { ListState } from '@qcwp/common'
import { articleAuthorPath, imageTransform } from '@qcwp/common'
import type { Comment } from '~~/src/server/modules/article/hook'
import type { AddArticleCommentResponse } from '~~/src/server/modules/article/type'
const props = withDefaults(defineProps<{
  item?: Comment | null
  rootCommentId?: string | null // 一级评论id
  isChild?: boolean // 是否是二级评论
  parentIndex?: number
  index?: number
  mediaNameMap?: Map<string, string>
}>(), {
  item: null,
  rootCommentId: null,
  isChild: false,
  mediaNameMap: () => new Map(),
})
const emits = defineEmits({
  addCommentAfter: (data: AddArticleCommentResponse) => typeof data === 'object',
})
const loadText: Record<ListState, string> = {
  finished: '没有更多了',
  loading: '加载中...',
  load: '加载更多',
  error: '加载失败',
}
const isShowInput = ref(false)
function formatArea(province: string, city: string) {
  const area = []
  if (province) {
    area.push(province.replace(/[省市]/, ''))
    if (city)
      area.push(city.replace(/[省市]/, ''))

    return Array.from(new Set(area)).join('')
  }
  else {
    return ''
  }
}

const mediaNames = computed(() => {
  const map = new Map()
  for (const [id, name] of props.mediaNameMap.entries())
    map.set(id, name)

  if (props.item?.reply?.list?.length) {
    props.item.reply.list.forEach((v) => {
      if (!map.has(v.mediaUserId))
        map.set(v.mediaUserId, v.mediaName)
    })
  }

  return map
})
function getMediaName(mediaId: string) {
  if (!mediaId)
    return '匿名用户'
  return props.mediaNameMap?.get(mediaId) || '匿名用户'
}
</script>

<template>
  <div v-if="item" flex items-start text-title-800>
    <NuxtLink target="_blank" no-rel :to="articleAuthorPath(item.mediaUserId)">
      <utils-avatar
        round
        color="var(--color-primary)"
        :size="props.isChild ? 'small' : 'large'"
        :name=" item.mediaName?.slice(0, 1) || '无'"
        :src="imageTransform(item.mediaAvatar, { width: 100, height: 100, format: 'webp' })"
      />
    </NuxtLink>
    <div flex-grow w-0 ml-2>
      <div flex items-center justify-between text-title-900>
        <h4 class="text-15px flex items-center text-3.5" md="text-4">
          <NuxtLink target="_blank" no-rel :to="articleAuthorPath(item.mediaUserId)">
            <p>{{ item.mediaName || '匿名用户' }}</p>
          </NuxtLink>
          <!-- 对于回复一级的回复，不显示回复 -->
          <template v-if="props.isChild && props.rootCommentId !== item.toCommentId">
            <span class="text-title-400 px-3 text-3 flex-shrink-0 leading-6" md="text-4">回复</span>
            <span>{{ getMediaName(item.toMediaUserId) }}</span>
          </template>
        </h4>
      </div>
      <p class="text-title-600 my-2.5 text-4">
        {{ item.content }}
      </p>
      <!-- TODO:回复评论内容 -->
      <!-- <p
        v-if="props.isChild && props.rootCommentId !== item.toMediaUserId"
        class="border-1 border-gray-200 py-2 px-3 text-gray-400 rounded-md mb-2.5 bg-gray-100 truncate"
      >
        {{ item.remark }}
      </p> -->
      <div>
        <div text-title-400 text-3 flex items-center gap-4 md="text-3.5">
          <p v-if="formatArea(item.provinceName, item.cityName)" class="text-title-500">
            {{ formatArea(item.provinceName, item.cityName) }}
          </p>
          <p class="text-title-500">
            {{ timeFrom(item.createTime) }}
          </p>
          <div class="text-title-500 cursor-pointer flex items-center hover:text-primary" @click="isShowInput = !isShowInput">
            <i class="mr-1 i-fluent:comment-28-filled  text-3.5" />
            <span v-show="!isShowInput">回复</span>
            <span v-show="isShowInput">取消回复</span>
          </div>
          <div
            v-if="!props.isChild && !['0', 'null', null].includes(item.replyCount)"
            class="text-title-800 cursor-pointer" @click="item?.changeShowReply"
          >
            <span>{{ item.replyCount }} 条回复</span>
            <i i-bxs:down-arrow text-2 ml-1 :class="{ 'rotate-180': item.showReply }" />
          </div>
          <!-- TODO:点赞 -->
          <!-- <div
            class="cursor-pointer flex items-center hover:text-primary"
            :class="{ 'text-primary': item.isGood }"
          >
            <i class="mr-1 i-icon-park-solid:good-two" />
            <span>{{ item.goodCount ? item.goodCount : '点赞' }}</span>
          </div> -->
        </div>
        <!-- input -->
        <div v-if="isShowInput" mt-2>
          <layout-comment-input
            v-model:show="isShowInput"
            :article-id="item.articleId"
            :comment-id="item.id"
            :comment-name="item.mediaName"
            :auto-focus="true"
            :option="{
              isChild,
              parentIndex,
              index,
              toCommentId: item.id,
              toMediaAvatar: item.mediaAvatar,
              toMediaName: item.mediaName,
              toMediaUserId: item.mediaUserId,
            }"
          />
        </div>
        <!-- children comment -->
        <ul v-if="item.showReply && !props.isChild && item.reply && item.reply.list" class="p-4 bg-gray-50 rounded-md mt-3">
          <li
            v-for="v, i in item.reply.list" :key="i"
            mb-4 pb-4 border="b-1 gray-200"
            last="border-b-0 mb-0 pb-0"
          >
            <LayoutCommentItem
              :item="v"
              :media-name-map="mediaNames"
              :root-comment-id="props.isChild ? props.rootCommentId : item.id"
              :is-child="true"
              :parent-index="index"
              :index="i"
            />
          </li>
          <div class="flex items-center justify-center cursor-pointer" @click="item?.reply?.getData">
            <van-loading v-if="item.reply.state === 'loading'" size="16" mr-2 />
            <span class="text-title-400">{{ loadText[item.reply.state] }}</span>
          </div>
        </ul>
      </div>
    </div>
  </div>
</template>
